import React, {useEffect, useRef, useState} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {getCSSVariableValue} from '../../../../_MeldiDham/assets/ts/_utils'
import {Link, useHistory} from 'react-router-dom'
import {IDashboardModel} from '../../../models/master-page/DashboardModel'
import {toast} from 'react-toastify'
import {getAdminCountApi} from '../../../modules/Dashboard/DashboardCURD'

type Props = {
  className: string
  chartColor: string
  strokeColor: string
  chartHeight: string
}

interface IState {
  loading: boolean
  list: IDashboardModel
}

/* ─── animated count-up hook ─── */
function useCountUp(target: number, duration = 1100, delay = 0) {
  const ref = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || !target) return
    const t = setTimeout(() => {
      const start = Date.now()
      const step = () => {
        const p = Math.min((Date.now() - start) / duration, 1)
        const ease = 1 - Math.pow(1 - p, 3)
        el.textContent = Math.round(ease * target).toLocaleString('en-IN')
        if (p < 1) requestAnimationFrame(step)
      }
      requestAnimationFrame(step)
    }, delay)
    return () => clearTimeout(t)
  }, [target, duration, delay])
  return ref
}

/* ─── single stat card ─── */
interface StatCardProps {
  value: number
  label: string
  sub: string
  accentColor: string
  iconBg: string
  iconColor: string
  subColor: string
  iconClass: string
  isLive?: boolean
  delay?: number
  linkTo?: string
}

const MemberStatCard: React.FC<StatCardProps> = ({
  value,
  label,
  sub,
  accentColor,
  iconBg,
  iconColor,
  subColor,
  iconClass,
  isLive,
  delay = 0,
  linkTo,
}) => {
  const spanRef = useCountUp(value, 1100, delay)
  const history = useHistory()
  return (
    <div
      onClick={() => linkTo && history.push(linkTo)}
      style={{
        flex: '1 1 0',
        minWidth: 150,
        background: '#fff',
        border: '1px solid #e8eaf0',
        borderRadius: 14,
        padding: '16px 18px 14px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        cursor: linkTo ? 'pointer' : 'default',
      }}
    >
      {/* left accent */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 4,
          height: '100%',
          background: accentColor,
          borderRadius: '14px 0 0 14px',
        }}
      />

      {/* icon */}
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 9,
          background: iconBg,
          color: iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          marginBottom: 10,
        }}
      >
        <i className={iconClass} />
      </div>

      <p
        style={{
          fontSize: 26,
          fontWeight: 700,
          margin: 0,
          color: '#111827',
          lineHeight: 1.1,
          fontFamily: 'inherit',
        }}
      >
        <span ref={spanRef}>0</span>
      </p>
      <p style={{fontSize: 15, color: '#6b7280', margin: '4px 0 0', fontFamily: 'inherit'}}>
        {label}
      </p>
      <p
        style={{
          fontSize: 13,
          margin: '5px 0 0',
          color: subColor,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          fontFamily: 'inherit',
        }}
      >
        {isLive && (
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#22c55e',
              display: 'inline-block',
              animation: 'mw2-pulse 1.3s ease-in-out infinite',
            }}
          />
        )}
        {sub}
      </p>
    </div>
  )
}

/* ════════════════════════════════════
   MAIN WIDGET
════════════════════════════════════ */
const MixedWidget2: React.FC<Props> = ({className, chartColor, chartHeight, strokeColor}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)

  const [state, setState] = useState<IState>({
    loading: false,
    list: {} as IDashboardModel,
  })

  useEffect(() => {
    setState((s) => ({...s, loading: true}))
    setTimeout(() => fetchAdminCountList(), 100)

    if (!chartRef.current) return
    const chart = new ApexCharts(
      chartRef.current,
      chartOptions(chartHeight, chartColor, strokeColor)
    )
    if (chart) chart.render()
    return () => {
      if (chart) chart.destroy()
    }
  }, [chartRef])

  function fetchAdminCountList() {
    getAdminCountApi()
      .then((response) => {
        const responseData = response.data
        if (responseData.isSuccess === true) {
          setState({list: responseData, loading: false})
        } else {
          toast.error(`${responseData.message}`)
          setState({list: {} as IDashboardModel, loading: false})
        }
      })
      .catch((error) => {
        toast.error(`${error}`)
        setState({list: {} as IDashboardModel, loading: false})
      })
  }

  const {list, loading} = state

  const memberLive = Number(list.memberLive) || 0
  const activeMember = Number(list.activeMember) || 0
  const notVerified = Number(list.notAciveMember) || 0

  return (
    <>
      {/* ── global keyframe ── */}
      <style>{`
        @keyframes mw2-pulse { 0%,100%{opacity:1} 50%{opacity:.25} }
        .mw2-live-badge {
          display:inline-flex;align-items:center;gap:5px;
          background:rgba(255,255,255,.15);border:1px solid rgba(255,255,255,.3);
          color:#fff;border-radius:20px;padding:3px 10px;font-size:11px;font-weight:600;
        }
        .mw2-tile { border-radius:12px; padding:18px 16px 14px; transition:transform .15s; }
        .mw2-tile:hover { transform:translateY(-2px); }
        .mw2-tile-val { font-size:2rem; font-weight:700; margin:0 0 6px; line-height:1; }
        .mw2-tile-link { font-size:13px; font-weight:600; text-decoration:none; display:flex; align-items:center; gap:5px; }
      `}</style>

      <div
        className={`card ${className}`}
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: 18,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
        }}
      >
        {/* ── HEADER ── */}
        <div
          className={`card-header border-0 py-4 bg-${chartColor}`}
          style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}
        >
          <div>
            <h3 className='card-title fw-bolder text-white mb-1' style={{fontSize: 18}}>
              <i className='bi bi-bar-chart-fill me-2' />
              Bareja Dham Statistics
            </h3>
            <p className='text-white mb-0' style={{fontSize: 12, opacity: 0.75}}>
              Live dashboard overview
            </p>
          </div>
          <span className='mw2-live-badge'>
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#22c55e',
                animation: 'mw2-pulse 1.3s ease-in-out infinite',
              }}
            />
            LIVE
          </span>
        </div>

        {/* ── APEX CHART ── */}
        <div className='card-body p-0'>
          <div
            ref={chartRef}
            className={`mixed-widget-2-chart card-rounded-bottom bg-${chartColor}`}
          />

          {/* ── STATS SECTION ── */}
          <div className='card-p mt-n20 position-relative'>
            {/* ── ROW 1: Member Stats (NEW) ── */}
            <div
              style={{
                display: 'flex',
                gap: 12,
                flexWrap: 'wrap',
                marginBottom: 16,
              }}
            >
              <MemberStatCard
                linkTo='/ram-nam-bank/member-live-report/list'
                value={memberLive}
                label='Live Members'
                sub='Currently in live jaap'
                accentColor='#1D9E75'
                iconBg='#E1F5EE'
                iconColor='#0F6E56'
                subColor='#0F6E56'
                iconClass='bi bi-activity'
                isLive
                delay={300}
              />
              <MemberStatCard
                linkTo='/ram-nam-bank/active-member/list'
                value={activeMember}
                label='Active Members'
                sub='Verified & active'
                accentColor='#185FA5'
                iconBg='#E6F1FB'
                iconColor='#185FA5'
                subColor='#185FA5'
                iconClass='bi bi-people-fill'
                delay={420}
              />

              <MemberStatCard
                linkTo='/ram-nam-bank/not-verified-member/list'
                value={notVerified}
                label='Not Verified'
                sub='Pending verification'
                accentColor='#BA7517'
                iconBg='#FAEEDA'
                iconColor='#854F0B'
                subColor='#BA7517'
                iconClass='bi bi-person-exclamation'
                delay={540}
              />
            </div>

            {/* ── DIVIDER ── */}
            <p
              style={{
                fontSize: 13,
                color: '#9ca3af',
                textTransform: 'uppercase',
                letterSpacing: '.07em',
                fontWeight: 700,
                margin: '0 0 12px',
              }}
            >
              Content overview
            </p>

            {/* ── ROW 2: Existing Content Tiles ── */}
            <div className='row g-4'>
              <div className='col-6 col-sm-6 col-md-4'>
                <div className='mw2-tile bg-light-warning'>
                  <p className='mw2-tile-val text-warning'>{loading ? '—' : list.event ?? 0}</p>
                  <Link to={'/master/event/list'} className='mw2-tile-link text-warning fs-5'>
                    <i className='bi bi-calendar-event' />
                    Events
                    <i className='bi bi-arrow-right' style={{fontSize: 14}} />
                  </Link>
                </div>
              </div>

              <div className='col-6 col-sm-6 col-md-4'>
                <div className='mw2-tile bg-light-primary'>
                  <p className='mw2-tile-val text-primary'>{loading ? '—' : list.blog ?? 0}</p>
                  <Link to={'/master/blog/list'} className='mw2-tile-link text-primary fs-5'>
                    <i className='bi bi-file-text' />
                    Blogs
                    <i className='bi bi-arrow-right' style={{fontSize: 14}} />
                  </Link>
                </div>
              </div>

              <div className='col-6 col-sm-6 col-md-4'>
                <div className='mw2-tile bg-light-dark'>
                  <p className='mw2-tile-val text-dark'>{loading ? '—' : list.album ?? 0}</p>
                  <Link to={'/master/album/list'} className='mw2-tile-link text-dark fs-5'>
                    <i className='bi bi-images' />
                    Albums
                    <i className='bi bi-arrow-right' style={{fontSize: 14}} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

/* ─── apex chart options (unchanged logic, cleaned up) ─── */
const chartOptions = (
  chartHeight: string,
  chartColor: string,
  strokeColor: string
): ApexOptions => {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const color = getCSSVariableValue('--bs-' + chartColor)

  return {
    chart: {
      fontFamily: 'inherit',
      type: 'area',
      height: chartHeight,
      toolbar: {show: false},
      zoom: {enabled: false},
      sparkline: {enabled: true},
      dropShadow: {
        enabled: true,
        enabledOnSeries: undefined,
        top: 5,
        left: 0,
        blur: 3,
        color: strokeColor,
        opacity: 0.5,
      },
    },
    plotOptions: {},
    legend: {show: false},
    dataLabels: {enabled: false},
    fill: {type: 'solid', opacity: 0},
    stroke: {curve: 'smooth', show: true, width: 3, colors: [strokeColor]},
    xaxis: {
      categories: ['Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'],
      axisBorder: {show: false},
      axisTicks: {show: false},
      labels: {show: false, style: {colors: labelColor, fontSize: '12px'}},
      crosshairs: {
        show: false,
        position: 'front',
        stroke: {color: borderColor, width: 1, dashArray: 3},
      },
    },
    yaxis: {
      min: 0,
      max: 80,
      labels: {show: false, style: {colors: labelColor, fontSize: '12px'}},
    },
    states: {
      normal: {filter: {type: 'none', value: 0}},
      hover: {filter: {type: 'none', value: 0}},
      active: {allowMultipleDataPointsSelection: false, filter: {type: 'none', value: 0}},
    },
    tooltip: {
      style: {fontSize: '12px'},
      y: {formatter: (val) => '$' + val + ' thousands'},
      marker: {show: false},
    },
    colors: ['transparent'],
    markers: {
      colors: [color],
      strokeColors: [strokeColor],
      strokeWidth: 3,
    },
  }
}

export {MixedWidget2}
